import { Card, Grid, FormControl } from "@mui/material";
import breakpoints from "assets/theme/base/breakpoints";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useMaterialUIController } from "context";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import Statistics from "./components/statistics";
import { useEffect, useState } from "react";
import PermitsTable from "./components/permitsTable";
import PermitSearch from "../reservations/ReservationSearch";
import PermitFilterByDate from "../reservations/ReservationFilterByDate";
import * as XLSX from "xlsx";
import moment from "moment";
import { getPermitStatistics } from "store/slice/permits/permitSlice";
import CreatePermit from "./components/createPermit";
import PermitSettings from "./components/permitSettings";
import PermitTypes from "./components/permitTypes";
import MDButton from "components/MDButton";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { getRatesByPlaceId } from "store/slice/rates/rateSlice";
import { getPermitsOptions } from "store/slice/permitsOptions/permitsOptionSlice";
import ReceipentTable from "./components/ReceipentTable";
import { getAutomatedReports } from "store/slice/automatedReports/automatedSlice";
import CustomTooltip from "components/Common/CustomTooltip";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && <MDBox sx={{ p: 3 }}>{children}</MDBox>}
    </div>
  );
}

const tabInfo = {
  all: "View all permits regardless of status.",
  active: "Permits that are currently valid and in use.",
  requested:
    "Permits that have been submitted and are awaiting approval or processing.",
  expired:
    "Permits that are no longer valid due to reaching their expiration date.",
  recipientEmail:
    "A newly created permit details from the chatbot has been sent to the added email address for approval.",
};

const Permit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const [receipentData, setReceipentData] = useState([]);
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const permitStatistics = useSelector((state) => state?.permit?.statistics);
  const permitList = useSelector((state) => state?.permit?.permitList || []);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [permitTypesOpen, setPermitTypesOpen] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get("tab");
  const [tabValue, setTabValue] = useState(activeTab ? activeTab : "all");
  const [page, setPage] = useState(0);
  const [openTooltipId, setOpenTooltipId] = useState(null);

  let placeId = selectedPlace?._id;
  const tz = selectedPlace?.timeZoneId || "America/Los_Angeles";
  if (!placeId) {
    placeId = localStorage.getItem("placeId");
  }
  const [filterOptions, setFilterOptions] = useState({
    pageNo: page,
    status: "all",
    placeId,
    startDate: "",
    endDate: "",
    search: "",
  });

  useEffect(() => {
    dispatch(getRatesByPlaceId(placeId));
  }, [dispatch, placeId]);

  useEffect(() => {
    function handleTabsOrientation() {
      return window.innerWidth < breakpoints.values.sm
        ? setTabsOrientation("vertical")
        : setTabsOrientation("horizontal");
    }

    window.addEventListener("resize", handleTabsOrientation);
    handleTabsOrientation();
    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  useEffect(() => {
    if (selectedPlace && selectedPlace._id) {
      dispatch(
        getPermitsOptions({
          placeId: selectedPlace._id,
        }),
      );
    }
  }, [dispatch, selectedPlace]);

  const handleGetReceipents = () => {
    dispatch(getAutomatedReports(placeId))
      .unwrap()
      .then((res) => {
        if (res?.success) {
          setReceipentData(res?.reports ?? []);
        }
      });
  };

  useEffect(() => {
    dispatch(getPermitStatistics(placeId));
    handleGetReceipents();
  }, [dispatch, placeId]);

  const handleSetTabValue = (event, newValue) => {
    setTabValue(newValue);
    queryParams.set("tab", newValue);
    const updatedQueryString = queryParams.toString();
    navigate(`?${updatedQueryString}`);
  };

  const handleExportExcel = () => {
    const wsData = [];
    const headers = [
      "Permit #",
      "Unit Number",
      "Email",
      "Phone Number",
      "Start Date",
      "End Date",
      "Status",
      "Permit Type",
      "Custom Rate",
      "Assigned Name",
      "License Plate(s)",
      "Vehicle State",
      "Vehicle Make",
      "Vehicle Model",
      "Vehicle Color",
    ];
    wsData.push(headers);

    permitList.forEach((permit) => {
      const startDate = permit.startDate
        ? moment(permit.startDate).format("MM-DD-YYYY")
        : "";
      const endDate = permit.endDate
        ? moment(permit.endDate).format("MM-DD-YYYY")
        : "";
      const end = moment(permit.endDate);
      const yesterday = moment().add(-1, "day");
      const isExpired = end.isBefore(yesterday);
      const status = isExpired
        ? "Expired"
        : permit.status
          ? permit.status.charAt(0).toUpperCase() + permit.status.slice(1)
          : "";

      const licensePlates =
        permit.licensePlate && permit.licensePlate.length > 0
          ? permit.licensePlate.join(", ")
          : "";

      wsData.push([
        permit.permitNumber || "",
        permit.internalId || "",
        permit.email || "",
        permit.phoneNo || "",
        startDate,
        endDate,
        status,
        permit.permitType || "",
        permit.rateId?.displayName || "",
        permit.assignedName || "",
        licensePlates,
        permit.vehicleState || "",
        permit.vehicleMake || "",
        permit.vehicleModel || "",
        permit.vehicleColor || "",
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Permits");
    XLSX.writeFile(wb, `Permits_List_${moment().format("MM-DD-YYYY")}.xlsx`);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={2} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={1}
                mt={-2}
                py={1}
                px={1}
                variant="gradient"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={12} md="auto">
                    <MDTypography variant="h6" color="white">
                      Permits
                    </MDTypography>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md
                    display="flex"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    flexWrap="wrap"
                    gap={1}
                  >
                    {selectedPlace && selectedPlace._id && (
                      <>
                        <MDButton
                          variant="outlined"
                          size="small"
                          className="me-2"
                          onClick={() => setPermitTypesOpen(true)}
                        >
                          Permit Types
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          size="small"
                          className="me-2"
                          onClick={() => setSettingsDialogOpen(true)}
                        >
                          Settings
                        </MDButton>

                        <MDButton
                          variant="outlined"
                          size="small"
                          onClick={() => setDialogOpen(true)}
                          className="me-2"
                        >
                          Create
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          size="small"
                          onClick={handleExportExcel}
                        >
                          Export Permits
                        </MDButton>
                      </>
                    )}
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox p={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <PlacePicker />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <PermitFilterByDate
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <PermitSearch
                        filterOptions={filterOptions}
                        setFilterOptions={setFilterOptions}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox p={2}>
                <Statistics data={permitStatistics} />
              </MDBox>

              <MDBox p={2}>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                    <AppBar position="static">
                      <Tabs
                        orientation={tabsOrientation}
                        value={tabValue}
                        onChange={handleSetTabValue}
                      >
                        <Tab
                          label={
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              All
                              <CustomTooltip
                                id="all"
                                title={tabInfo.all}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                              />
                            </MDBox>
                          }
                          value="all"
                        />
                        <Tab
                          label={
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              Active
                              <CustomTooltip
                                id="active"
                                title={tabInfo.active}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                              />
                            </MDBox>
                          }
                          value="active"
                        />

                        <Tab
                          label={
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              Requested
                              <CustomTooltip
                                id="requested"
                                title={tabInfo.requested}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                              />
                            </MDBox>
                          }
                          value="requested"
                        />

                        <Tab
                          label={
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              Expired
                              <CustomTooltip
                                id="expired"
                                title={tabInfo.expired}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                              />
                            </MDBox>
                          }
                          value="expired"
                        />
                        <Tab
                          label={
                            <MDBox display="flex" alignItems="center" gap={0.5}>
                              Recipient Email
                              <CustomTooltip
                                id="recipientEmail"
                                title={tabInfo.recipientEmail}
                                openTooltipId={openTooltipId}
                                setOpenTooltipId={setOpenTooltipId}
                              />
                            </MDBox>
                          }
                          value="receipent"
                        />
                      </Tabs>
                    </AppBar>
                    <TabPanel value={tabValue} index="all">
                      {placeId && (
                        <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                          <PermitsTable
                            placeId={placeId}
                            startDate={filterOptions.startDate}
                            endDate={filterOptions.endDate}
                            search={filterOptions.search}
                            activeTab={tabValue}
                            tz={tz}
                          />
                        </Grid>
                      )}
                    </TabPanel>
                    <TabPanel value={tabValue} index="active">
                      {placeId && (
                        <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                          <PermitsTable
                            placeId={placeId}
                            startDate={filterOptions.startDate}
                            endDate={filterOptions.endDate}
                            search={filterOptions.search}
                            activeTab={tabValue}
                            tz={tz}
                          />
                        </Grid>
                      )}
                    </TabPanel>
                    <TabPanel value={tabValue} index="requested">
                      {placeId && (
                        <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                          <PermitsTable
                            placeId={placeId}
                            startDate={filterOptions.startDate}
                            endDate={filterOptions.endDate}
                            search={filterOptions.search}
                            activeTab={tabValue}
                            tz={tz}
                          />
                        </Grid>
                      )}
                    </TabPanel>
                    <TabPanel value={tabValue} index="expired">
                      {placeId && (
                        <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                          <PermitsTable
                            placeId={placeId}
                            startDate={filterOptions.startDate}
                            endDate={filterOptions.endDate}
                            search={filterOptions.search}
                            activeTab={tabValue}
                            tz={tz}
                          />
                        </Grid>
                      )}
                    </TabPanel>
                    <TabPanel value={tabValue} index="receipent">
                      {placeId && (
                        <Grid item xs={12} md={12} lg={12} sx={{ ml: "auto" }}>
                          <ReceipentTable
                            receipentData={receipentData}
                            updateParentData={handleGetReceipents}
                          />
                        </Grid>
                      )}
                    </TabPanel>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <CreatePermit
        placeId={placeId}
        dialogOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        startDate={filterOptions.startDate}
        endDate={filterOptions.endDate}
        search={filterOptions.search}
        activeTab={tabValue}
        tz={tz}
      />

      <PermitSettings
        dialogOpen={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
      />

      <PermitTypes
        dialogOpen={permitTypesOpen}
        onClose={() => setPermitTypesOpen(false)}
      />
      <Footer />
    </DashboardLayout>
  );
};

export default Permit;
